import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { getRealtimeVoiceCallCopy } from '@/i18n/realtime-voice-call-copy';

type FloatingVoiceDockLabels = {
  activeCall: string;
  ready: string;
  expand: string;
  collapse: string;
  openChat: string;
  endCall: string;
  endingCall: string;
  holdToSpeak: string;
  moveHint: string;
  voiceChat: string;
  microphoneMode: string;
  autoModeHint: string;
  pttModeHint: string;
};

const LABELS: Record<SupportedLocale, FloatingVoiceDockLabels> = {
  en: {
    endingCall: 'Ending call…',
    ready: 'Ready',
    activeCall: 'Active voice call',
    expand: 'Show voice call details',
    collapse: 'Hide voice call details',
    openChat: 'Open task',
    endCall: 'End voice call',
    holdToSpeak: 'Hold to speak',
    moveHint: 'Drag to move. Use arrow keys to adjust the position.',
    voiceChat: 'Voice chat',
    microphoneMode: 'Microphone mode',
    autoModeHint: 'Speak any time. Mute when you need privacy.',
    pttModeHint: 'Hold the microphone button while you speak.',
  },
  ar: {
    endingCall: 'جارٍ إنهاء المكالمة…',
    ready: 'جاهز',
    activeCall: 'مكالمة صوتية نشطة',
    expand: 'عرض تفاصيل المكالمة الصوتية',
    collapse: 'إخفاء تفاصيل المكالمة الصوتية',
    openChat: 'فتح المهمة',
    endCall: 'إنهاء المكالمة الصوتية',
    holdToSpeak: 'اضغط باستمرار للتحدث',
    moveHint: 'اسحب للتحريك. استخدم مفاتيح الأسهم لضبط الموضع.',
    voiceChat: 'محادثة صوتية',
    microphoneMode: 'وضع الميكروفون',
    autoModeHint: 'تحدث في أي وقت. اكتم الميكروفون عند الحاجة.',
    pttModeHint: 'اضغط باستمرار على زر الميكروفون أثناء التحدث.',
  },
  de: {
    endingCall: 'Anruf wird beendet…',
    ready: 'Bereit',
    activeCall: 'Aktiver Sprachanruf',
    expand: 'Details zum Sprachanruf anzeigen',
    collapse: 'Details zum Sprachanruf ausblenden',
    openChat: 'Aufgabe öffnen',
    endCall: 'Sprachanruf beenden',
    holdToSpeak: 'Zum Sprechen gedrückt halten',
    moveHint:
      'Zum Verschieben ziehen. Mit den Pfeiltasten die Position anpassen.',
    voiceChat: 'Sprachchat',
    microphoneMode: 'Mikrofonmodus',
    autoModeHint:
      'Sprechen Sie jederzeit. Schalten Sie das Mikrofon bei Bedarf stumm.',
    pttModeHint: 'Halten Sie die Mikrofontaste beim Sprechen gedrückt.',
  },
  es: {
    endingCall: 'Finalizando llamada…',
    ready: 'Listo',
    activeCall: 'Llamada de voz activa',
    expand: 'Mostrar detalles de la llamada',
    collapse: 'Ocultar detalles de la llamada',
    openChat: 'Abrir tarea',
    endCall: 'Finalizar llamada de voz',
    holdToSpeak: 'Mantén pulsado para hablar',
    moveHint: 'Arrastra para mover. Usa las flechas para ajustar la posición.',
    voiceChat: 'Chat de voz',
    microphoneMode: 'Modo de micrófono',
    autoModeHint:
      'Habla cuando quieras. Silencia el micrófono si necesitas privacidad.',
    pttModeHint: 'Mantén pulsado el botón del micrófono mientras hablas.',
  },
  fr: {
    endingCall: 'Fin de l’appel…',
    ready: 'Prêt',
    activeCall: 'Appel vocal actif',
    expand: 'Afficher les détails de l’appel',
    collapse: 'Masquer les détails de l’appel',
    openChat: 'Ouvrir la tâche',
    endCall: 'Terminer l’appel vocal',
    holdToSpeak: 'Maintenir pour parler',
    moveHint:
      'Faites glisser pour déplacer. Utilisez les flèches pour ajuster la position.',
    voiceChat: 'Chat vocal',
    microphoneMode: 'Mode du micro',
    autoModeHint:
      'Parlez à tout moment. Coupez le micro pour plus de confidentialité.',
    pttModeHint:
      'Maintenez le bouton du micro enfoncé pendant que vous parlez.',
  },
  hi: {
    endingCall: 'कॉल समाप्त हो रही है…',
    ready: 'तैयार',
    activeCall: 'सक्रिय वॉइस कॉल',
    expand: 'वॉइस कॉल का विवरण दिखाएँ',
    collapse: 'वॉइस कॉल का विवरण छिपाएँ',
    openChat: 'कार्य खोलें',
    endCall: 'वॉइस कॉल समाप्त करें',
    holdToSpeak: 'बोलने के लिए दबाकर रखें',
    moveHint:
      'स्थान बदलने के लिए खींचें। स्थिति समायोजित करने के लिए तीर कुंजियाँ दबाएँ।',
    voiceChat: 'वॉइस चैट',
    microphoneMode: 'माइक्रोफ़ोन मोड',
    autoModeHint: 'कभी भी बोलें। ज़रूरत पड़ने पर माइक्रोफ़ोन म्यूट करें।',
    pttModeHint: 'बोलते समय माइक्रोफ़ोन बटन दबाकर रखें।',
  },
  id: {
    endingCall: 'Mengakhiri panggilan…',
    ready: 'Siap',
    activeCall: 'Panggilan suara aktif',
    expand: 'Tampilkan detail panggilan suara',
    collapse: 'Sembunyikan detail panggilan suara',
    openChat: 'Buka tugas',
    endCall: 'Akhiri panggilan suara',
    holdToSpeak: 'Tahan untuk berbicara',
    moveHint:
      'Seret untuk memindahkan. Gunakan tombol panah untuk menyesuaikan posisi.',
    voiceChat: 'Chat suara',
    microphoneMode: 'Mode mikrofon',
    autoModeHint: 'Bicara kapan saja. Bisukan mikrofon saat perlu privasi.',
    pttModeHint: 'Tahan tombol mikrofon saat berbicara.',
  },
  it: {
    endingCall: 'Chiusura chiamata…',
    ready: 'Pronto',
    activeCall: 'Chiamata vocale attiva',
    expand: 'Mostra i dettagli della chiamata',
    collapse: 'Nascondi i dettagli della chiamata',
    openChat: 'Apri attività',
    endCall: 'Termina chiamata vocale',
    holdToSpeak: 'Tieni premuto per parlare',
    moveHint: 'Trascina per spostare. Usa le frecce per regolare la posizione.',
    voiceChat: 'Chat vocale',
    microphoneMode: 'Modalità microfono',
    autoModeHint: 'Parla quando vuoi. Disattiva il microfono se serve privacy.',
    pttModeHint: 'Tieni premuto il pulsante del microfono mentre parli.',
  },
  ja: {
    endingCall: '通話を終了中…',
    ready: '準備完了',
    activeCall: '音声通話中',
    expand: '音声通話の詳細を表示',
    collapse: '音声通話の詳細を閉じる',
    openChat: 'タスクを開く',
    endCall: '音声通話を終了',
    holdToSpeak: '長押しして話す',
    moveHint: 'ドラッグして移動。矢印キーで位置を調整できます。',
    voiceChat: '音声チャット',
    microphoneMode: 'マイクモード',
    autoModeHint:
      'いつでも話せます。必要に応じてマイクをミュートしてください。',
    pttModeHint: '話している間、マイクボタンを押し続けてください。',
  },
  ko: {
    endingCall: '통화 종료 중…',
    ready: '준비됨',
    activeCall: '진행 중인 음성 통화',
    expand: '음성 통화 정보 표시',
    collapse: '음성 통화 정보 숨기기',
    openChat: '작업 열기',
    endCall: '음성 통화 종료',
    holdToSpeak: '길게 눌러 말하기',
    moveHint: '끌어서 이동하세요. 방향키로 위치를 조정하세요.',
    voiceChat: '음성 채팅',
    microphoneMode: '마이크 모드',
    autoModeHint: '언제든 말하세요. 필요할 때 마이크를 음소거하세요.',
    pttModeHint: '말하는 동안 마이크 버튼을 길게 누르세요.',
  },
  'pt-BR': {
    endingCall: 'Encerrando chamada…',
    ready: 'Pronto',
    activeCall: 'Chamada de voz ativa',
    expand: 'Mostrar detalhes da chamada',
    collapse: 'Ocultar detalhes da chamada',
    openChat: 'Abrir tarefa',
    endCall: 'Encerrar chamada de voz',
    holdToSpeak: 'Segure para falar',
    moveHint: 'Arraste para mover. Use as setas para ajustar a posição.',
    voiceChat: 'Chat de voz',
    microphoneMode: 'Modo do microfone',
    autoModeHint:
      'Fale a qualquer momento. Silencie o microfone quando precisar.',
    pttModeHint: 'Segure o botão do microfone enquanto fala.',
  },
  ru: {
    endingCall: 'Завершение вызова…',
    ready: 'Готово',
    activeCall: 'Активный голосовой вызов',
    expand: 'Показать сведения о вызове',
    collapse: 'Скрыть сведения о вызове',
    openChat: 'Открыть задачу',
    endCall: 'Завершить голосовой вызов',
    holdToSpeak: 'Удерживайте, чтобы говорить',
    moveHint: 'Перетащите для перемещения. Стрелки меняют положение.',
    voiceChat: 'Голосовой чат',
    microphoneMode: 'Режим микрофона',
    autoModeHint:
      'Говорите в любое время. При необходимости выключайте микрофон.',
    pttModeHint: 'Удерживайте кнопку микрофона, пока говорите.',
  },
  vi: {
    endingCall: 'Đang kết thúc cuộc gọi…',
    ready: 'Sẵn sàng',
    activeCall: 'Cuộc gọi thoại đang diễn ra',
    expand: 'Hiện chi tiết cuộc gọi',
    collapse: 'Ẩn chi tiết cuộc gọi',
    openChat: 'Mở tác vụ',
    endCall: 'Kết thúc cuộc gọi thoại',
    holdToSpeak: 'Giữ để nói',
    moveHint: 'Kéo để di chuyển. Dùng phím mũi tên để chỉnh vị trí.',
    voiceChat: 'Trò chuyện thoại',
    microphoneMode: 'Chế độ micro',
    autoModeHint: 'Nói bất cứ lúc nào. Tắt micro khi cần riêng tư.',
    pttModeHint: 'Giữ nút micro trong khi nói.',
  },
  zh: {
    endingCall: '正在结束通话…',
    ready: '就绪',
    activeCall: '语音通话中',
    expand: '显示语音通话详情',
    collapse: '隐藏语音通话详情',
    openChat: '打开任务',
    endCall: '结束语音通话',
    holdToSpeak: '按住说话',
    moveHint: '拖动以移动。使用方向键调整位置。',
    voiceChat: '语音聊天',
    microphoneMode: '麦克风模式',
    autoModeHint: '随时说话。需要隐私时可将麦克风静音。',
    pttModeHint: '说话时按住麦克风按钮。',
  },
  'zh-TW': {
    endingCall: '正在結束通話…',
    ready: '就緒',
    activeCall: '語音通話中',
    expand: '顯示語音通話詳情',
    collapse: '隱藏語音通話詳情',
    openChat: '開啟任務',
    endCall: '結束語音通話',
    holdToSpeak: '按住說話',
    moveHint: '拖曳以移動。使用方向鍵調整位置。',
    voiceChat: '語音聊天',
    microphoneMode: '麥克風模式',
    autoModeHint: '隨時說話。需要隱私時可將麥克風靜音。',
    pttModeHint: '說話時按住麥克風按鈕。',
  },
};

export function getFloatingVoiceDockCopy(locale: string | null | undefined) {
  return {
    ...getRealtimeVoiceCallCopy(locale),
    ...LABELS[normalizeLocale(locale)],
  };
}
